import { SearchLgIcon, XCloseIcon } from '@/components/ui/icons'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput
} from '@/components/ui/input-group'
import { cn } from '@/helpers/utils'

interface SearchInputProps {
	value: string
	onChange: (value: string) => void
	placeholder: string
	/** aria-label for the trailing clear button. */
	clearLabel: string
	className?: string
	/** When set, the trailing button also dismisses the field (mobile only). */
	onDismiss?: () => void
	/** Focus the input on mount (mobile: the field opens ready to type into). */
	autoFocus?: boolean
	onFocus?: () => void
	onBlur?: () => void
}

/**
 * Rounded search field with a leading magnifier and a trailing clear/dismiss
 * button. Shared by the check-in and attendees toolbars.
 */
export function SearchInput({
	value,
	onChange,
	placeholder,
	clearLabel,
	className,
	onDismiss,
	autoFocus,
	onFocus,
	onBlur
}: SearchInputProps) {
	// Without a dismiss handler the button is a plain clear, so it only makes
	// sense once there's something to clear.
	const showTrailing = !!value || !!onDismiss

	return (
		<InputGroup
			className={cn(
				'h-10 min-w-0 rounded-full border-grey-600',
				className
			)}
		>
			<InputGroupAddon>
				<SearchLgIcon />
			</InputGroupAddon>
			<InputGroupInput
				autoFocus={autoFocus}
				onBlur={onBlur}
				onChange={(e) => onChange(e.target.value)}
				onFocus={onFocus}
				placeholder={placeholder}
				value={value}
			/>
			{showTrailing && (
				<InputGroupAddon align='inline-end'>
					<InputGroupButton
						aria-label={clearLabel}
						className='bg-transparent hover:bg-grey-200'
						onClick={() => {
							onChange('')
							onDismiss?.()
						}}
						size='icon-xs'
						variant='icon'
					>
						<XCloseIcon />
					</InputGroupButton>
				</InputGroupAddon>
			)}
		</InputGroup>
	)
}
